/**
 * GeneralStorePanel — a persistent NPC shop.
 *
 * Curated stock at fixed prices; sells any inventory item back at 40% of
 * the reference price. Stock is a shared world resource — it depletes as
 * players buy and slowly refills.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Store, Coins, ShoppingBag, Send, X, Search, Package, ArrowUpDown, Backpack,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { useGame } from "@/context/GameContext";

const CAT_LABEL = {
  weapon: "Weapons", ammo: "Ammo", food: "Food", raw_fish: "Raw fish",
  rune: "Runes", material: "Materials", tool: "Tools", ore: "Ores",
  logs: "Logs", misc: "Misc",
};

function TradeDialog({ open, onClose, mode, item, maxQty, onSubmit }) {
  const [qty, setQty] = useState(1);
  useEffect(() => { if (open) setQty(1); }, [open]);
  if (!open || !item) return null;
  const unit = mode === "buy" ? item.buy_price : item.sell_price;
  const total = unit * Math.max(1, Math.min(qty, maxQty));

  const submit = async (e) => {
    e?.preventDefault?.();
    const n = Math.max(1, Math.min(Number(qty) || 1, maxQty));
    onSubmit(n);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="shop-trade-modal"
    >
      <form onSubmit={submit} className="panel w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-lg heading-gilded flex items-center gap-2">
            {mode === "buy"
              ? <><ShoppingBag size={16} className="text-amber-400" /> Buy {item.name}</>
              : <><Coins size={16} className="text-amber-400" /> Sell {item.name}</>}
          </h3>
          <button type="button" onClick={onClose} className="text-stone-500 hover:text-amber-300"><X size={18} /></button>
        </div>
        <div className="text-xs font-body text-stone-400 mb-3">
          {mode === "buy"
            ? `Shopkeeper has ${item.stock.toLocaleString()} in stock at ${item.buy_price} gp each.`
            : `You have ${maxQty.toLocaleString()} in your bag. Shopkeeper pays ${item.sell_price} gp each.`}
        </div>
        <input
          type="number"
          min={1}
          max={maxQty}
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          className="w-full bg-stone-950 border border-stone-700 rounded px-3 py-2 text-lg font-numeric text-amber-300 focus:border-amber-600 outline-none"
          data-testid="shop-trade-qty"
        />
        <div className="flex gap-1.5 mt-2">
          {[1, 10, 100, "MAX"].map((v) => (
            <button
              type="button"
              key={v}
              onClick={() => setQty(v === "MAX" ? maxQty : Math.min(v, maxQty))}
              className="flex-1 text-[11px] font-body px-2 py-1 rounded border border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800 transition-colors"
            >
              {typeof v === "number" ? `x${v}` : "Max"}
            </button>
          ))}
        </div>
        <div className="mt-3 flex items-center justify-between text-sm font-numeric">
          <span className="text-stone-500 uppercase tracking-widest text-[10px]">
            {mode === "buy" ? "Total cost" : "Total earned"}
          </span>
          <span className={mode === "buy" ? "text-red-300" : "text-emerald-300"}>
            {mode === "buy" ? "-" : "+"}{total.toLocaleString()} gp
          </span>
        </div>
        <button
          type="submit"
          className={
            "w-full mt-4 px-4 py-2 rounded font-body flex items-center justify-center gap-2 transition-colors " +
            (mode === "buy"
              ? "bg-amber-700 hover:bg-amber-600 text-stone-950"
              : "bg-emerald-800 hover:bg-emerald-700 text-stone-100")
          }
          data-testid="shop-trade-submit"
        >
          <Send size={14} /> {mode === "buy" ? "Buy" : "Sell"}
        </button>
      </form>
    </div>
  );
}

export function GeneralStorePanel() {
  const { snap } = useGame();
  const [stock, setStock] = useState([]);
  const [gold, setGold] = useState(0);
  const [sellRatio, setSellRatio] = useState(0.4);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("buy"); // buy | sell
  const [trade, setTrade] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/game/shop/general");
      setStock(data.stock);
      setGold(data.gold);
      setSellRatio(data.sell_ratio);
    } catch (e) { toast.error("Shop unavailable", { description: formatApiError(e) }); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Silent restock heartbeat.
  useEffect(() => {
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  const buy = async (item, qty) => {
    try {
      const { data } = await api.post("/api/game/shop/general/buy", { item_id: item.item_id, qty });
      toast.success(`Bought ${qty}× ${item.name}`, { description: `-${data.spent.toLocaleString()} gp` });
      setTrade(null);
      load();
    } catch (e) { toast.error("Purchase failed", { description: formatApiError(e) }); }
  };
  const sell = async (item, qty) => {
    try {
      const { data } = await api.post("/api/game/shop/general/sell", { item_id: item.item_id, qty });
      toast.success(`Sold ${qty}× ${item.name}`, { description: `+${data.earned.toLocaleString()} gp` });
      setTrade(null);
      load();
    } catch (e) { toast.error("Sale failed", { description: formatApiError(e) }); }
  };

  const filteredBuy = useMemo(() => {
    const n = q.trim().toLowerCase();
    return stock.filter((s) => !n || s.name.toLowerCase().includes(n) || s.cat.includes(n));
  }, [stock, q]);

  // Sell view: every item in the player's bag (not just tracked stock).
  const inventory = useMemo(() => (snap?.inventory || []).map((row) => {
    const listed = stock.find((s) => s.item_id === row.item);
    return {
      item_id: row.item,
      name:    listed?.name || row.item.replace(/_/g, " "),
      cat:     listed?.cat || "misc",
      sell_price: listed?.sell_price || Math.max(1, Math.floor((listed?.buy_price || 2) * sellRatio)),
      buy_price:  listed?.buy_price || 2,
      stock:      listed?.stock || 0,
      in_bag:     row.qty,
    };
  }), [snap?.inventory, stock, sellRatio]);

  const filteredSell = useMemo(() => {
    const n = q.trim().toLowerCase();
    return inventory.filter((s) => !n || s.name.toLowerCase().includes(n));
  }, [inventory, q]);

  const rows = tab === "buy" ? filteredBuy : filteredSell;

  return (
    <div className="p-4 lg:p-6 space-y-4" data-testid="shop-panel">
      {/* Header */}
      <div className="panel overflow-hidden">
        <div className="p-4 sm:p-6 flex items-center gap-3 sm:gap-6"
             style={{ backgroundImage: "linear-gradient(180deg, rgba(12,10,9,0.4), rgba(12,10,9,0.92))" }}>
          <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-lg border-2 border-amber-700 bg-stone-950 flex items-center justify-center">
            <Store size={30} className="text-amber-400" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-display text-2xl sm:text-4xl heading-gilded">General Store</h1>
            <p className="text-stone-400 font-body italic text-xs sm:text-sm">
              &ldquo;Ye may look, ye may buy, ye may barter — but no lifting.&rdquo;
            </p>
            <div className="mt-1 text-xs sm:text-sm font-numeric text-stone-300 flex items-center gap-3">
              <span>Purse · <span className="text-amber-300">{gold.toLocaleString()} gp</span></span>
              <span>Shopkeeper buys at <span className="text-amber-300">{Math.round(sellRatio * 100)}%</span></span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs + search */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1">
          {[
            { id: "buy",  label: "Buy",  icon: ShoppingBag },
            { id: "sell", label: "Sell", icon: Backpack   },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={
                "flex items-center gap-1.5 text-xs font-body px-3 py-1.5 rounded border transition-colors " +
                (tab === t.id
                  ? "border-amber-700 bg-amber-950/40 text-amber-100"
                  : "border-stone-700 text-stone-400 hover:text-amber-300 hover:border-amber-800")
              }
              data-testid={`shop-tab-${t.id}`}
            >
              <t.icon size={12} /> {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px] max-w-md">
          <Search size={14} className="text-stone-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={tab === "buy" ? "Search shop inventory…" : "Search your bag…"}
            className="flex-1 bg-stone-950 border border-stone-700 rounded px-2 py-1 text-sm font-body focus:border-amber-600 outline-none"
            data-testid="shop-search"
          />
        </div>
      </div>

      {/* Rows */}
      <div className="panel p-2 sm:p-3">
        {loading ? (
          <div className="text-stone-500 italic py-8 text-center font-body">Fetching wares…</div>
        ) : rows.length === 0 ? (
          <div className="text-stone-500 italic py-8 text-center font-body">
            {tab === "buy" ? "No wares match your search." : "Your bag is empty — nothing to sell."}
          </div>
        ) : (
          <ul className="divide-y divide-stone-900">
            {rows.map((s) => (
              <li
                key={s.item_id}
                className="flex items-center gap-3 px-2 py-2 hover:bg-stone-900/50 transition-colors"
                data-testid={`shop-row-${s.item_id}`}
              >
                <div className="w-10 h-10 rounded bg-stone-950 border border-stone-800 flex items-center justify-center shrink-0">
                  <Package size={16} className="text-stone-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-body text-sm text-stone-100 truncate">
                    {s.name}
                    <span className="text-stone-600 font-numeric text-[10px] ml-2 uppercase tracking-widest">
                      {CAT_LABEL[s.cat] || s.cat}
                    </span>
                  </div>
                  <div className="text-[11px] font-numeric text-stone-500">
                    Buy <span className="text-amber-400">{s.buy_price}g</span>
                    <span className="mx-1.5">·</span>
                    Sell <span className="text-emerald-400">{s.sell_price}g</span>
                    <span className="mx-1.5">·</span>
                    {tab === "buy"
                      ? <>Stock <span className="text-stone-300">{s.stock}</span></>
                      : <>In bag <span className="text-stone-300">{s.in_bag}</span></>}
                  </div>
                </div>
                {tab === "buy" ? (
                  <button
                    onClick={() => setTrade({ mode: "buy", item: s, maxQty: Math.min(s.stock, Math.floor(gold / s.buy_price) || 0) })}
                    disabled={s.stock <= 0 || gold < s.buy_price}
                    className="text-[11px] font-body px-2.5 py-1 rounded border border-amber-800 text-amber-300 hover:bg-amber-950/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`shop-buy-${s.item_id}`}
                  >
                    Buy
                  </button>
                ) : (
                  <button
                    onClick={() => setTrade({ mode: "sell", item: s, maxQty: s.in_bag })}
                    disabled={s.in_bag <= 0}
                    className="text-[11px] font-body px-2.5 py-1 rounded border border-emerald-800 text-emerald-300 hover:bg-emerald-950/30 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`shop-sell-${s.item_id}`}
                  >
                    Sell
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <TradeDialog
        open={!!trade}
        onClose={() => setTrade(null)}
        mode={trade?.mode}
        item={trade?.item}
        maxQty={trade?.maxQty || 0}
        onSubmit={(qty) => (trade?.mode === "buy" ? buy(trade.item, qty) : sell(trade.item, qty))}
      />
    </div>
  );
}
